<template>
  <div :class="`pdl-tabs__nav-wrap is-${tabPosition}`">
    <div ref="navScroll" class="pdl-tabs__nav-scroll" :class="isHorizontalClasses">
      <div
        ref="nav"
        :class="`pdl-tabs__nav is-${tabPosition} ${alignmentClass}`"
        role="tablist"
        :style="navStyle"
        @keydown="changeTab"
      >
        <tab-bar :alignment="alignment" :tab-position="tabPosition" :tabs="panes" />
        <div
          v-for="(pane, index) of panes"
          :id="`tab-${pane.tabName}`"
          :key="index"
          ref="tabs"
          :qaid="`tab-${pane.tabName}`"
          role="tab"
          :class="[
            `pdl-tabs__item is-${tabPosition}`,
            {'is-focus': isFocus},
            {'is-active': pane.tabName === activeTabName},
          ]"
          :tabindex="pane.tabName === activeTabName ? '0' : '-1'"
          @focus="setFocus"
          @blur="removeFocus"
          @click="
            (ev) => {
              removeFocus();
              onTabClick(pane, ev);
              updateScroll(ev);
              switchTab(pane.tabName);
            }
          "
        >
          <component v-if="pane.navNode" :is="pane.navNode" />
          <div v-else>{{ pane.label }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TabBar from './TabBar.vue';

const keys = {
  LEFT_ARROW: 37,
  UP_ARROW: 38,
  RIGHT_ARROW: 39,
  DOWN_ARROW: 40,
};
export default {
  name: 'TabNav',

  components: {
    TabBar,
  },

  inject: ['activeTabName', 'switchTab', 'navWidth'],

  props: {
    panes: {
      type: Array,
      default: () => [],
    },
    navNodes: {
      type: Array,
      default: () => [],
    },
    onTabClick: {
      type: Function,
      default: () => [],
    },
    alignment: {
      type: String,
      default: 'start',
    },
    tabPosition: {
      type: String,
      default: 'top',
    },
  },

  data() {
    return {
      navOffset: 0,
      isFocus: false,
      isActive: false,
    };
  },

  computed: {
    alignmentClass() {
      if (this.isNavOverflowing) {
        return '';
      }
      return this.alignment ? `is-${this.alignment}` : '';
    },
    navStyle() {
      return {
        transform: `translate${this.direction}(${this.navOffset}px)`,
      };
    },
    direction() {
      return ['top', 'bottom'].indexOf(this.tabPosition) !== -1 ? 'X' : 'Y';
    },
    isHorizontalClasses() {
      return {
        'overflow-x-auto overflow-y-hidden w-full max-w-full': this.direction === 'X',
      };
    },
    isNavOverflowing() {
      const END = 'end';
      if (this.alignment === END && this.navWidth) {
        return this.$refs?.tabs?.[0]?.offsetLeft < 0;
      }
      return this.navWidth < this.$refs?.nav?.scrollWidth;
    },
  },
  methods: {
    updateScroll(event) {
      if (!this.$refs.nav || !this.$refs.navScroll) return;
      const scrollBehavior = !window.matchMedia(`(prefers-reduced-motion: reduce)`).matches ? 'smooth' : 'instant';
      event.target.scrollIntoView({behavior: scrollBehavior, inline: 'center', block: 'nearest'});
    },

    setFocus() {
      this.isFocus = true;
    },

    removeFocus() {
      this.isFocus = false;
    },

    changeTab(e) {
      const keyCode = e.keyCode;
      let nextIndex, currentIndex;
      let tabList;

      if ([keys.LEFT_ARROW, keys.UP_ARROW, keys.RIGHT_ARROW, keys.DOWN_ARROW].indexOf(keyCode) !== -1) {
        tabList = e.currentTarget.querySelectorAll('[role=tab]');
        currentIndex = Array.prototype.indexOf.call(tabList, e.target);
      } else {
        return;
      }

      if (keyCode === keys.LEFT_ARROW || keyCode === keys.UP_ARROW) {
        // left
        if (currentIndex === 0) {
          // first
          nextIndex = tabList.length - 1;
        } else {
          nextIndex = currentIndex - 1;
        }
      } else {
        // right
        if (currentIndex < tabList.length - 1) {
          // not last
          nextIndex = currentIndex + 1;
        } else {
          nextIndex = 0;
        }
      }

      tabList[nextIndex].click();
      tabList[nextIndex].focus();
      this.setFocus();
    },
  },
};
</script>
<style lang="scss">
@import 'Tabs.scss';
</style>
